import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import ShootDaysPanel from '../components/ShootDaysPanel';
import { DayFilterPills } from '../components/ProjectDays';
import { api } from '../api';
import GearListTab from './tabs/GearListTab';
import PackingTab from './tabs/PackingTab';
import ReturnTab from './tabs/ReturnTab';
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Building2,
  CheckCircle2,
  Circle,
  Trash2,
  RotateCcw,
  Link2Off,
  Printer,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Project detail page — header, PM-link metadata, ShootDays + day filter, and
 * the three tabs (Gear List / Packing / Return). The bulk of each tab's
 * implementation lives in `./tabs/*.jsx`; this file is intentionally kept
 * thin so adding tab-specific features doesn't bloat the shared scaffold.
 */
export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    projects,
    completeProject,
    reactivateProject,
    deleteProject,
    unlinkProjectFromPm,
    projectProgress,
  } = useApp();
  const project = projects.find((p) => p.id === id);
  const [tab, setTab] = useState('list');
  const [refreshing, setRefreshing] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null); // null = "All days"
  // Hoisted from ShootDaysPanel so ShootDaysPanel + DayFilterPills share one
  // source of truth. This keeps the day-pill row in sync with the visible
  // shoot days after PM marks a day complete (wrap-day filter).
  const [visibleDays, setVisibleDays] = useState([]);

  useEffect(() => {
    if (!project?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const ds = await api.listProjectDays(project.id);
        if (!cancelled) setVisibleDays(ds || []);
      } catch {
        if (!cancelled) setVisibleDays([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [project?.id, project?.updatedAt, project?.externalSyncedAt]);

  const handleRefreshFromPM = async () => {
    if (!project) return;
    setRefreshing(true);
    try {
      const { api } = await import('../api');
      const updated = await api.refreshProjectFromPm(project.id);
      // The context's projects array will pick it up via re-fetch, but for now mutate via the API directly:
      window.location.reload();
      void updated;
    } catch (e) {
      if (e.status === 410) {
        toast.error(
          e.message ||
            "This project's link to Project Manager is broken (upstream project deleted). Gear and history are unchanged.",
          { duration: 7000 },
        );
        setTimeout(() => window.location.reload(), 700);
      } else {
        toast.error(e.message || 'Refresh failed');
      }
    } finally {
      setRefreshing(false);
    }
  };

  const handleUnlinkFromPM = async () => {
    if (!project) return;
    const ok = window.confirm(
      'Warning: This will sever the link to Whiplash Projects. Changes will no longer sync between apps. Are you sure?',
    );
    if (!ok) return;
    setUnlinking(true);
    try {
      await unlinkProjectFromPm(project.id);
      toast.success('Converted to standalone project');
    } catch (e) {
      toast.error(e.message || 'Could not convert');
    } finally {
      setUnlinking(false);
    }
  };

  if (!project) {
    return (
      <div className="p-8 text-center">
        <p className="text-neutral-400 mb-3">Project not found.</p>
        <Link to="/" className="text-amber-400 hover:underline text-sm">Back to Projects</Link>
      </div>
    );
  }

  const { totalUnits, packedUnits } = projectProgress(project);
  const pct = totalUnits === 0 ? 0 : Math.round((packedUnits / totalUnits) * 100);
  // "PM-managed" = linked to Whiplash Projects AND the upstream link is still
  // healthy. When true, we hide destructive local actions so PM stays the
  // source of truth: archiving is done by marking the project completed in
  // PM (which flows in as a status sync), and delete flows from a PM delete.
  const isPmManaged = !!(project.externalProjectId && !project.externalLinkBroken);
  const isCompleted = project.status === 'completed';
  const allReturned = packedUnits === 0;

  return (
    <div className="p-5">
      {/* Header */}
      <button
        onClick={() => navigate('/')}
        className="text-xs text-neutral-500 hover:text-white inline-flex items-center gap-1 mb-3"
      >
        <ArrowLeft size={13} /> Projects
      </button>

      <div className="surface rounded-lg p-5 mb-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              {isCompleted ? (
                <CheckCircle2 size={14} className="text-emerald-400" />
              ) : (
                <Circle size={14} className="text-amber-400" />
              )}
              <span
                className={`text-[10px] tracking-[0.25em] uppercase font-medium ${
                  isCompleted ? 'text-emerald-400' : 'text-amber-400'
                }`}
              >
                {isCompleted ? 'Completed' : 'Active'}
              </span>
              {isPmManaged && (
                <span
                  title="This project is managed by Whiplash Projects. Complete or delete it there to keep both apps in sync. To edit it independently here, use Convert to Standalone below."
                  className="text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 rounded bg-neutral-900 border border-neutral-800 text-neutral-400"
                  data-testid="pm-managed-status-badge"
                >
                  PM-managed
                </span>
              )}
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">{project.name}</h1>
            {project.externalProjectId && (
              project.externalLinkBroken ? (
                <div
                  className="inline-flex items-center gap-2 mb-2 text-[10px] tracking-wider uppercase px-2 py-0.5 rounded bg-rose-500/10 border border-rose-500/30 text-rose-300"
                  title="The upstream PM project no longer exists (deleted on the PM side). Gear and history are unchanged."
                  data-testid="pm-link-broken-banner"
                >
                  <span>● PM link broken</span>
                  <span className="text-rose-400/70 normal-case tracking-normal">
                    upstream project deleted — gear & history unchanged
                  </span>
                  <button
                    onClick={handleUnlinkFromPM}
                    disabled={unlinking}
                    title="Detach this project from Whiplash Projects and keep it as a standalone project here. Gear and history stay intact."
                    className="text-rose-300 hover:text-white ml-1 underline decoration-dotted disabled:opacity-50 normal-case tracking-normal"
                    data-testid="pm-unlink-btn-broken"
                  >
                    {unlinking ? 'Converting…' : 'Convert to Standalone'}
                  </button>
                </div>
              ) : (
                <div className="inline-flex items-center gap-1.5 mb-2 text-[10px] tracking-wider uppercase px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-300">
                  <span>● Synced from Project Manager</span>
                  <button
                    onClick={handleRefreshFromPM}
                    disabled={refreshing}
                    title="Refresh fields from Project Manager"
                    className="text-amber-400 hover:text-amber-200 ml-1 disabled:opacity-50"
                  >
                    ↻ {refreshing ? 'Refreshing…' : 'Refresh'}
                  </button>
                  <span className="text-amber-500/40">·</span>
                  <button
                    onClick={handleUnlinkFromPM}
                    disabled={unlinking}
                    title="Detach this project from Whiplash Projects and keep it as a standalone project here. Gear, days, and history stay intact; future PM updates won't affect it."
                    className="text-amber-400 hover:text-amber-200 ml-1 inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid="pm-unlink-btn"
                  >
                    <Link2Off size={9} /> {unlinking ? 'Converting…' : 'Convert to Standalone'}
                  </button>
                </div>
              )
            )}
            <div className="flex flex-wrap gap-4 text-xs text-neutral-400">
              {project.jobNumber && (
                <span className="inline-flex items-center gap-1.5 font-mono">
                  #{project.jobNumber}
                </span>
              )}
              {project.director && (
                <span className="inline-flex items-center gap-1.5">
                  Dir: {project.director}
                </span>
              )}
              {project.client && (
                <span className="inline-flex items-center gap-1.5">
                  <Building2 size={12} className="text-neutral-600" /> {project.client}
                </span>
              )}
              {project.location && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin size={12} className="text-neutral-600" /> {project.location}
                </span>
              )}
              {project.shootDates?.length > 0 && (
                <span className="inline-flex items-center gap-1.5">
                  <Calendar size={12} className="text-neutral-600" /> {project.shootDates.join(' → ')}
                </span>
              )}
            </div>
            {project.notes && (
              <p className="text-xs text-neutral-500 mt-3 leading-relaxed max-w-2xl">{project.notes}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(`/projects/${project.id}/pull-sheet`)}
              title="Print pull sheet"
              className="text-xs px-2.5 py-1.5 rounded-md border border-neutral-800 text-neutral-300 hover:bg-neutral-900 hover:text-white flex items-center gap-1.5"
            >
              <Printer size={12} /> Pull Sheet
            </button>
            {!isCompleted ? (
              !isPmManaged && (
                <button
                  disabled={!allReturned}
                  onClick={async () => {
                    try {
                      await completeProject(project.id);
                      toast.success('Project marked complete');
                    } catch (e) {
                      toast.error(e.message || 'Could not complete');
                    }
                  }}
                  title={!allReturned ? 'Check in all gear first' : ''}
                  className={`text-xs px-3 py-1.5 rounded-md flex items-center gap-1.5 ${
                    !allReturned
                      ? 'border border-neutral-800 text-neutral-600 cursor-not-allowed'
                      : 'border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10'
                  }`}
                  data-testid="mark-complete-btn"
                >
                  <CheckCircle2 size={13} /> Mark Complete
                </button>
              )
            ) : (
              !isPmManaged && (
                <button
                  onClick={async () => {
                    try {
                      await reactivateProject(project.id);
                      toast.success('Project reactivated');
                    } catch (e) {
                      toast.error(e.message || 'Could not reactivate');
                    }
                  }}
                  className="text-xs px-3 py-1.5 rounded-md border border-amber-500/30 text-amber-400 hover:bg-amber-500/10 flex items-center gap-1.5"
                >
                  <RotateCcw size={13} /> Reactivate
                </button>
              )
            )}
            <button
              disabled={isPmManaged}
              onClick={async () => {
                if (isPmManaged) return;
                if (window.confirm(`Delete project "${project.name}"? This cannot be undone.`)) {
                  try {
                    await deleteProject(project.id);
                    navigate('/');
                    toast.success('Project deleted');
                  } catch (e) {
                    toast.error(e.message || 'Could not delete');
                  }
                }
              }}
              title={
                isPmManaged
                  ? 'Managed by Whiplash Projects — delete the project there to trigger a clean sync deletion.'
                  : 'Delete project'
              }
              className={`text-xs px-2.5 py-1.5 rounded-md border flex items-center gap-1.5 ${
                isPmManaged
                  ? 'border-neutral-900 text-neutral-700 cursor-not-allowed'
                  : 'border-neutral-800 text-neutral-400 hover:bg-rose-500/10 hover:text-rose-400 hover:border-rose-500/30'
              }`}
              data-testid="delete-project-btn"
            >
              <Trash2 size={12} />
            </button>
          </div>
        </div>

        {!isCompleted && totalUnits > 0 && (
          <div className="mt-5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] tracking-wider uppercase text-neutral-500">Packing</span>
              <span className="text-[11px] text-neutral-300 font-medium">
                {packedUnits} / {totalUnits} · {pct}%
              </span>
            </div>
            <div className="h-1.5 bg-neutral-900 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-500 rounded-full transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}
      </div>

      <ShootDaysPanel project={project} days={visibleDays} />

      <DayFilterPills
        dayNumbers={visibleDays.map((d) => d.dayNumber).filter(Boolean)}
        selectedDay={selectedDay}
        onSelect={setSelectedDay}
      />

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4 border-b border-neutral-900">
        {[
          { id: 'list', label: 'Gear List' },
          { id: 'pack', label: 'Packing' },
          { id: 'return', label: 'Return' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-xs border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? 'border-amber-500 text-amber-400'
                : 'border-transparent text-neutral-500 hover:text-neutral-300'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'list' && <GearListTab project={project} selectedDay={selectedDay} />}
      {tab === 'pack' && <PackingTab project={project} selectedDay={selectedDay} />}
      {tab === 'return' && <ReturnTab project={project} />}
    </div>
  );
}
